import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Phone, Mail, MapPin, Scale, Shield, ChevronRight, Menu, X } from "lucide-react";
import { Toaster } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navbar Component
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "Our Team", href: "#team" },
    { name: "Practice Areas", href: "#services" },
    { name: "Media", href: "#media" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-obsidian-900/95 backdrop-blur-md border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Empty space for balance */}
          <div className="w-32"></div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-sm text-white/70 hover:text-gold-500 transition-colors duration-300 uppercase tracking-wider"
              >
                {link.name}
              </a>
            ))}
            <a
              href="tel:250-361-3000"
              className="flex items-center gap-2 bg-gold-500 text-obsidian-900 px-6 py-3 text-sm font-semibold uppercase tracking-wide hover:bg-gold-400 transition-colors duration-300"
              data-testid="nav-call-btn"
            >
              <Phone className="w-4 h-4" />
              24/7 Call
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-obsidian-800 border-t border-white/5 py-6">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 px-4 text-white/70 hover:text-gold-500 transition-colors duration-300 uppercase tracking-wider text-sm"
              >
                {link.name}
              </a>
            ))}
            <a
              href="tel:250-361-3000"
              className="flex items-center justify-center gap-2 bg-gold-500 text-obsidian-900 mx-4 mt-4 px-6 py-3 text-sm font-semibold uppercase tracking-wide"
            >
              <Phone className="w-4 h-4" />
              24/7 Call
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center hero-bg"
      style={{
        backgroundImage: `url('/courthouse.jpg')`,
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-obsidian-900/85 via-obsidian-900/60 to-obsidian-900/40"></div>
      
      <div className="relative z-10 flex flex-col items-center text-center">
        <a 
          href="#team" 
          className="group cursor-pointer"
          data-testid="hero-logo-link"
        >
          <img 
            src="/logo_dark_bg.png" 
            alt="Claus Drury Roy - Criminal Defence" 
            className="h-24 md:h-28 lg:h-32 w-auto transition-all duration-500 group-hover:scale-110 group-hover:drop-shadow-[0_0_40px_rgba(212,175,55,0.6)] group-hover:brightness-110"
          />
          <p className="mt-6 text-sm uppercase tracking-[0.3em] text-white/0 group-hover:text-gold-500 transition-all duration-300">
            Meet Our Team
          </p>
        </a>
      </div>
    </section>
  );
};

// Team Section
const TeamSection = () => {
  const [selectedLawyer, setSelectedLawyer] = useState(null);
  
  const lawyers = [
    {
      name: "Robert Claus, KC",
      role: "Lawyer",
      image: "https://customer-assets.emergentagent.com/job_attorney-web-1/artifacts/74m2qv08_Image.png",
      bio: "Bob obtained his L.L.B. from the University of Victoria and has practiced exclusively in the area of criminal defence for over 40 years. He has successfully represented clients accused of first degree murder, second degree murder, manslaughter and criminal organization charges.\n\nHis successful practice has focused extensively on wiretap law and he was counsel in the landmark wiretap litigation case of R. v. Araujo, heard before the Supreme Court of Canada in 2000. As well, much of his practice has focused on impaired driving, drug cases and the largest \"gang\" prosecution at the time, R. v. Trang, before the Court of Queen's Bench in Alberta.\n\nBob has appeared in trial courts in British Columbia, Alberta, Saskatchewan, Ontario, the Northwest Territories, and the Yukon. He has appeared in appellate courts in British Columbia, Alberta and the Northwest Territories. He has also appeared in the Supreme Court of Canada.\n\nBob is a member of the Trial Lawyers Association of British Columbia and he is a past coach of the moot trial team at the University of Victoria's Faculty of Law.\n\nIn 2021, Bob was awarded the King's Counsel (KC) designation by the Attorney General of British Columbia.",
      featured: true,
    },
    {
      name: "Ryan Drury",
      role: "Lawyer",
      image: "https://customer-assets.emergentagent.com/job_attorney-web-1/artifacts/pcb7m9a7_DSC_6622%202.jpg",
      bio: "Ryan has been defending criminal allegations since 2010 after graduating from the University of Victoria.\n\nRyan has successfully defended serious charges in the British Columbia Provincial Court and Supreme Court, including murder, manslaughter, aggravated assault, sexual assault and possession for the purpose of trafficking. He has also argued appeals at the British Columbia Court of Appeal.\n\nRyan is dedicated to defending civil liberties. He also teaches Criminal Procedure at the University of Victoria.",
    },
    {
      name: "Schuyler Roy",
      role: "Lawyer",
      image: "https://customer-assets.emergentagent.com/job_attorney-web-1/artifacts/hfmv4x20_Schuyler.png",
      bio: "Originally from Saskatchewan, Schuyler obtained his law degree from the University of Victoria. After articling with a prominent criminal defence firm in Vancouver, Schuyler returned to Vancouver Island where he has practiced criminal defence ever since.\n\nSchuyler has successfully defended clients facing serious allegations such as murder, drug trafficking, aggravated assault, sexual assault, robbery, as well as numerous other types of offences. He also has experience negotiating with Crown Counsel and police to prevent criminal charges from being laid.\n\nSchuyler is passionate about criminal defence and has spent the last several years coaching UVic Law's mock criminal trial team. He also previously lived in Spain and is able to assist clients in Spanish.",
    },
    {
      name: "Joel Feinman",
      role: "Lawyer",
      image: "https://customer-assets.emergentagent.com/job_attorney-web-1/artifacts/n3njwr66_IMG_3725.jpeg",
      bio: "Joel obtained his law degree from the University of Arizona in 2007 and has been practicing criminal law ever since. Before moving to Victoria in 2022 and being called to the B.C. Bar in March 2026, Joel lived in the USA and worked as a public defender in Tucson, Arizona for over fourteen years. There he conducted over 40 jury trials, and represented clients accused of drug crime, assault, sex crime, kidnapping, manslaughter, first and second degree murder, and capital murder. Joel also taught law at the University of Arizona, and currently teaches law at the University of Victoria.\n\nA life-long advocate for civil rights and criminal justice reform, Joel is committed to ensuring his clients receive justice and are treated with dignity and respect.",
    },
    {
      name: "Alexandra Goldsmith",
      role: "Articled Student",
      image: "https://customer-assets.emergentagent.com/job_a4e11934-8b45-4ce0-9330-b5fce4f00737/artifacts/jwsl7upz_Alex%20brick%20image.png",
      bio: "Originally from Saskatchewan, Alexandra earned her law degree from the University of Saskatchewan. Prior to law school, she completed a Bachelor of Arts in Sociology with Great Distinction and an Ethics, Justice and Law Certificate, developing a strong interest in the social context of the law and its impact on individuals and communities.\n\nAlexandra's interest in criminal defence is rooted in a commitment to effective client advocacy and the belief that individuals who are unable to speak for themselves deserve a meaningful voice within the legal system. This commitment has guided her longstanding community involvement, including volunteering in the senior community and participating in the Best Buddies Program throughout high school and university.\n\nAlexandra gained hands-on experience working at the Court of King's Bench during the summers of 2023 and 2024 and throughout the 2023–2024 academic year. She is focused on building a career centered on principled, effective advocacy within the criminal justice system.",
    },
    {
      name: "Tess Aguiar",
      role: "Office Manager",
      image: "https://customer-assets.emergentagent.com/job_attorney-web-1/artifacts/s8nvkt20_Tess.jpg",
      bio: "Tess brings over a decade of administrative expertise to the team, ensuring smooth and efficient operations behind the scenes. Since 2023, she has worked with Claus Drury Roy, supporting the firm's mission to provide dedicated and strategic criminal defence services. Her extensive experience and attention to detail help keep cases organized and clients well-informed throughout the legal process.",
    },
  ];

  return (
    <section id="team" data-testid="team-section" className="py-24 md:py-32 bg-obsidian-900">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-gold-500 mb-4">Our Team</p>
          <h2 className="text-3xl md:text-4xl text-white tracking-wide font-semibold uppercase">
            Experienced<br />
            <span className="text-gold-500">Defence Lawyers</span>
          </h2>
          <p className="text-white/60 leading-relaxed mt-4">
            We have decades of experience running complex criminal trials. You can trust us with your defence.
          </p>
        </div>

        {/* Lawyers - Top Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {lawyers.slice(0, 3).map((lawyer, index) => (
            <div
              key={lawyer.name}
              data-testid={`lawyer-card-${index}`}
              onClick={() => setSelectedLawyer(lawyer)}
              className="profile-card bg-obsidian-800 border border-white/5 hover:border-gold-500/30 transition-all duration-500 group cursor-pointer"
            >
              <div className="relative overflow-hidden h-96">
                <img
                  src={lawyer.image}
                  alt={lawyer.name}
                  className={`w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 ${
                    lawyer.name === "Schuyler Roy" 
                      ? "object-[center_0%] scale-125 group-hover:scale-[1.35]" 
                      : "object-top group-hover:scale-105"
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian-900 via-obsidian-900/50 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                  <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-2">{lawyer.role}</p>
                  <h3 className="font-serif text-xl text-white mb-2">{lawyer.name}</h3>
                  <p className="text-sm text-gold-500/80 group-hover:text-gold-500 transition-colors duration-300">
                    Click to view full profile
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Staff - Bottom Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {lawyers.slice(3).map((lawyer, index) => (
            <div
              key={lawyer.name}
              data-testid={`lawyer-card-${index + 3}`}
              onClick={() => setSelectedLawyer(lawyer)}
              className="profile-card bg-obsidian-800 border border-white/5 hover:border-gold-500/30 transition-all duration-500 group cursor-pointer"
            >
              <div className="relative overflow-hidden h-72">
                <img
                  src={lawyer.image}
                  alt={lawyer.name}
                  className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian-900 via-obsidian-900/50 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                  <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-2">{lawyer.role}</p>
                  <h3 className="font-serif text-xl text-white mb-2">{lawyer.name}</h3>
                  <p className="text-sm text-gold-500/80 group-hover:text-gold-500 transition-colors duration-300">
                    Click to view full profile
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lawyer Profile Modal */}
      <Dialog open={!!selectedLawyer} onOpenChange={() => setSelectedLawyer(null)}>
        <DialogContent className="max-w-3xl bg-obsidian-800 border-white/10 p-0 overflow-hidden">
          {selectedLawyer && (
            <div className="flex flex-col md:flex-row">
              <div className="md:w-2/5 h-64 md:h-auto">
                <img
                  src={selectedLawyer.image}
                  alt={selectedLawyer.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="md:w-3/5 p-6 md:p-8 max-h-[60vh] overflow-y-auto">
                <DialogHeader>
                  <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-2">{selectedLawyer.role}</p>
                  <DialogTitle className="font-serif text-2xl md:text-3xl text-white mb-4">
                    {selectedLawyer.name}
                  </DialogTitle>
                </DialogHeader>
                <DialogDescription className="text-white/70 leading-relaxed whitespace-pre-line text-sm md:text-base">
                  {selectedLawyer.bio}
                </DialogDescription>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    { name: "Murder & Manslaughter" },
    { name: "Drug Offences" },
    { name: "Sexual Assault" },
    { name: "Assault & Violent Crimes" },
    { name: "Impaired Driving" },
    { name: "Fraud & Theft" },
    { name: "Firearms Offences" },
    { name: "Misunderstandings" },
    { name: "Wrongful Accusations" },
  ];

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-obsidian-800">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div className="lg:sticky lg:top-32">
            <p className="text-xs uppercase tracking-[0.3em] text-gold-500 mb-4">Practice Areas</p>
            <h2 className="text-3xl md:text-4xl text-white tracking-wide font-semibold uppercase mb-6">
              Complete<br />
              <span className="text-gold-500">Criminal Defence</span>
            </h2>
            <p className="text-white/60 leading-relaxed mb-8">
              We negotiate with Crown Counsel and the police to prevent criminal charges from being laid. We have successfully defended clients accused of the most serious crimes.
            </p>
          </div>

          <div className="space-y-0">
            {services.map((service, index) => (
              <div
                key={service.name}
                data-testid={`service-item-${index}`}
                className="border-b border-white/10 py-6"
              >
                <h3 className="text-base font-semibold text-white uppercase tracking-widest">
                  {service.name}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Media Section
const MediaSection = () => {
  const mediaStories = [
    {
      title: "Schuyler's client avoids jail; Crown sought 5.5 years",
      publication: "CanLII",
      url: "https://www.canlii.org/en/bc/bcsc/doc/2025/2025bcsc2616/2025bcsc2616.html?resultId=5fc90630809b490697d881ce9e429287&searchId=2026-03-10T17:27:41:903/815fa41ef02a49f28c8e0ffaba831ae5",
    },
    {
      title: "Duncan man found not guilty of manslaughter",
      publication: "Victoria News",
      url: "https://vicnews.com/2025/07/31/man-found-not-guilty-of-manslaughter-in-2022-death-near-ditidaht/",
    },
    {
      title: "B.C. judge says self-defence can include expelling someone who has outstayed welcome",
      publication: "Times Colonist",
      url: "https://www.timescolonist.com/national-news/bc-judge-says-self-defence-can-include-expelling-someone-who-has-outstayed-welcome-11486963",
    },
    {
      title: "Not Guilty: Judge acquits man accused of sexual assault",
      publication: "CanLII",
      url: "https://www.canlii.org/en/bc/bcsc/doc/2025/2025bcsc535/2025bcsc535.html?resultId=69a9192644ce483c917831a39c82d4db&searchId=2026-03-20T11:03:00:888/23e00516403344889249b338123d0f87",
    },
    {
      title: "Langford man charged with second-degree murder found not criminally responsible",
      publication: "Times Colonist",
      url: "https://www.timescolonist.com/local-news/langford-man-not-criminally-responsible-for-moms-murder-victims-brother-blames-mental-health-system-10365392",
    },
    {
      title: "Judge acquits Nanaimo man of sexual touching",
      publication: "CanLII",
      url: "https://www.canlii.org/en/bc/bcsc/doc/2024/2024bcsc2333/2024bcsc2333.html?resultId=37e4f1dfb6c9411986ecbfea74f8218f&searchId=2026-02-28T12:28:10:247/18f9db2568f94f41903ecdb8548647a9",
    },
    {
      title: "Duncan man avoids jail when Crown sought 4 years",
      publication: "CanLII",
      url: "https://www.canlii.org/en/bc/bcsc/doc/2024/2024bcsc660/2024bcsc660.html?resultId=3008747e954643d9a3e2406f6680b405&searchId=2026-02-28T12:22:01:864/08dede68da4347fba6add0931a85c7b8",
    },
    {
      title: "Nanaimo woman avoids jail in brazen workplace scam",
      publication: "Nanaimo News Now",
      url: "https://nanaimonewsnow.com/2024/01/15/nanaimo-woman-convicted-of-brazen-workplace-scam/",
    },
    {
      title: "Defence lawyer in trial of escaped B.C. inmate says Crown did not prove guilt",
      publication: "CBC",
      url: "https://www.cbc.ca/news/canada/british-columbia/trial-of-escaped-b-c-inmate-1.6684556",
    },
    {
      titleJsx: <>Ryan appointed as <em>Amicus Curiae</em> to assist Duncan man in sex-assault trial</>,
      publication: "Times Colonist",
      url: "https://www.timescolonist.com/local-news/judge-appoints-lawyer-to-assist-duncan-man-in-sex-assault-trial-4663126",
    },
  ];

  return (
    <section id="media" data-testid="media-section" className="py-24 md:py-32 bg-obsidian-800">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-gold-500 mb-4">In The News</p>
          <h2 className="text-3xl md:text-4xl text-white tracking-wide font-semibold uppercase">
            Recent Media
          </h2>
        </div>

        <div className="space-y-6">
          {mediaStories.map((story, index) => (
            <a
              key={index}
              href={story.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`media-story-${index}`}
              className="block p-6 bg-obsidian-900 border border-white/5 hover:border-gold-500/30 transition-all duration-300 group"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-gold-500 mb-2">{story.publication}</p>
              <h3 className="text-lg text-white group-hover:text-gold-500 transition-colors duration-300">
                {story.titleJsx || story.title}
              </h3>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" data-testid="contact-section" className="py-20 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="flex flex-col sm:flex-row justify-center items-center gap-8 sm:gap-12">
          <a
            href="tel:250-361-3000"
            className="group flex items-center gap-3 text-white hover:text-gold-500 transition-colors duration-300"
            data-testid="contact-phone"
          >
            <Phone className="w-5 h-5 text-gold-500 flex-shrink-0" />
            <span className="text-base font-semibold uppercase tracking-widest whitespace-nowrap">250-361-3000</span>
          </a>

          <a
            href="mailto:reception@cdcdlaw.com"
            className="group flex items-center gap-3 text-white hover:text-gold-500 transition-colors duration-300"
            data-testid="contact-email"
          >
            <Mail className="w-5 h-5 text-gold-500 flex-shrink-0" />
            <span className="text-base font-semibold uppercase tracking-widest whitespace-nowrap">reception@cdcdlaw.com</span>
          </a>

          <a 
            href="https://www.google.com/maps/search/?api=1&query=301+-+754+Broughton+Street,+Victoria,+BC"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 text-white hover:text-gold-500 transition-colors duration-300"
            data-testid="contact-address"
          >
            <MapPin className="w-5 h-5 text-gold-500 flex-shrink-0" />
            <span className="text-base font-semibold uppercase tracking-widest whitespace-nowrap">301 - 754 Broughton Street, Victoria, BC</span>
          </a>
        </div>

        <p
          data-testid="contact-disclaimer"
          className="mt-10 text-center text-xs text-white/40 italic tracking-wide"
        >
          *Claus Drury Roy is a collection of independent law practices
        </p>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-12 bg-obsidian-800 border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex justify-center">
          <p className="text-sm text-white/40">
            © {new Date().getFullYear()} Claus Drury Roy. Criminal Defence Lawyers. Victoria, BC.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  return (
    <div className="relative">
      <div className="noise-overlay"></div>
      <Navbar />
      <HeroSection />
      <div className="section-divider"></div>
      <TeamSection />
      <div className="section-divider"></div>
      <ServicesSection />
      <div className="section-divider"></div>
      <MediaSection />
      <div className="section-divider"></div>
      <ContactSection />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#0F0F0F',
            color: '#F5F5F5',
            border: '1px solid rgba(212, 175, 55, 0.3)',
          },
        }}
      />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
